import { Navigate, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import './App.css';
import Main from '../Main/Main.js';
import AllNews from '../AllNews/AllNews.js';
import NewsPage from '../NewsPage/NewsPage.js';
import PageNotFound from '../PageNotFound/PageNotFound.js';
import CookieConsentPopup from '../CookieConsentPopup/CookieConsentPopup.js';
import AboutUs from '../AboutUs/AboutUs';
import Contacts from '../Contacts/Contacts';
import VolunteersPage from '../VolunteersPage/VolunteersPage';
import VdpoHistoryPage from '../VdpoHistoryPage/VdpoHistoryPage';
import SociallyActivitiesPage from '../SociallyActivitiesPage/SociallyActivitiesPage';
import CompetitionsPage from '../CompetitionsPage/CompetitionsPage';
import EducationPage from '../EducationPage/EducationPage';
import EducationDocumentsPage from '../EducationDocumentsPage/EducationDocumentsPage';
import PrivacyPolicyPage from '../PrivacyPolicyPage/PrivacyPolicyPage';
import CookiesPolicyPage from '../CookiesPolicyPage/CookiesPolicyPage';
import ServicesPage from '../ServicesPage/ServicesPage.jsx';
import PersonalDataConsentPage from '../PersonalDataConsentPage/PersonalDataConsentPage.js';
import StreletsMonitoringPage from '../StreletsMonitoringPage/StreletsMonitoringPage.jsx';
import AdminPage from '../AdminPage/AdminPage.jsx';
import GamePage from '../GamePage/GamePage.jsx';
import AppealsInstructionsPage from '../AppealsInstructionsPage/AppealsInstructionsPage.jsx';
import StreletsRequestPage from '../StreletsRequestPage/StreletsRequestPage.jsx';
import YandexMetricaTracker from '../YandexMetricaTracker/YandexMetricaTracker.js';
import { COOKIE_CONSENT_KEY } from '../../utils/yandexMetrica.js';
import {
  getCanonicalUrl,
  toCanonicalLocation,
  toCanonicalPath,
} from '../../utils/canonicalRoutes.js';

function isReactSnapPrerender() {
  return navigator.userAgent === 'ReactSnap';
}

function ScrollToTopOnRouteChange() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return null;
}

function CanonicalUrlHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const canonicalLocation = toCanonicalLocation(location);

    if (canonicalLocation !== `${location.pathname}${location.search}${location.hash}`) {
      navigate(canonicalLocation, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    const canonicalUrl = getCanonicalUrl(location);
    const existingLink = document.querySelector('link[rel="canonical"]');

    if (!canonicalUrl) {
      existingLink?.remove();
      return;
    }

    const canonicalLink = existingLink || document.createElement('link');

    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', canonicalUrl);

    if (!existingLink) {
      document.head.appendChild(canonicalLink);
    }
  }, [location]);

  return null;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const isPrerender = isReactSnapPrerender();
  const [hasAnalyticsConsent, setHasAnalyticsConsent] = useState(
    () => localStorage.getItem(COOKIE_CONSENT_KEY) === 'true'
  );
  const isAdminPage = location.pathname === '/admin'
    || location.pathname.startsWith('/admin/');
  const canonicalPathname = toCanonicalPath(location.pathname);
  const isGamePage = canonicalPathname === '/game/';

  function handleBack() {
    navigate(-1);
  }

  return (
    <div className="app">
      <ScrollToTopOnRouteChange />
      <CanonicalUrlHandler />
      {!isPrerender && <YandexMetricaTracker hasConsent={hasAnalyticsConsent} />}
      {!isPrerender && !isAdminPage && !isGamePage && (
        <CookieConsentPopup onAccept={() => setHasAnalyticsConsent(true)} />
      )}
      <Routes>
        <Route path="/" element={<Main />} />
        <Route path="/all-news/" element={<AllNews />} />
        <Route path="/news/:id/" element={<NewsPage onBack={handleBack} />} />
        <Route
          path="/all-news/archive/"
          element={
            <PageNotFound
              textError="Страница на доработке"
              statusError="Разработка"
              onBack={handleBack}
            />
          }
        />
        <Route path="/about-us/" element={<AboutUs onBack={handleBack} />} />
        <Route path="/privacy-policy/" element={<PrivacyPolicyPage />} />
        <Route path="/cookies-policy/" element={<CookiesPolicyPage />} />
        <Route
          path="/personal-data-consent/"
          element={<PersonalDataConsentPage />}
        />
        <Route
          path="/volunteers/"
          element={<VolunteersPage onBack={handleBack} />}
        />
        <Route path="/vdpo-history/" element={<VdpoHistoryPage />} />
        <Route
          path="/education/"
          element={<EducationPage />}
        />
        <Route
          path="/education/documents/"
          element={<EducationDocumentsPage />}
        />
        <Route path="/contacts/" element={<Contacts onBack={handleBack} />} />
        <Route path="/services/" element={<ServicesPage />} />
        <Route path="/services/:slug/" element={<ServicesPage />} />
        <Route
          path="/strelets-monitoring/"
          element={<StreletsMonitoringPage />}
        />
        <Route
          path="/strelets-monitoring/appeals/"
          element={<AppealsInstructionsPage />}
        />
        <Route
          path="/strelets-monitoring/request/"
          element={<StreletsRequestPage />}
        />
        <Route
          path="/services/strelets-monitoring/"
          element={<Navigate to="/strelets-monitoring/" replace />}
        />
        <Route
          path="/shop/"
          element={
            <PageNotFound
              statusError="Разработка"
              textError="Страница на доработке"
              onBack={handleBack}
            />
          }
        />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/game/" element={<GamePage />} />
        <Route
          path="/personal-account/"
          element={
            <PageNotFound
              statusError="Разработка"
              textError="Страница на доработке"
              onBack={handleBack}
            />
          }
        />
        <Route
          path="/socially/"
          element={<SociallyActivitiesPage />}
        />
        <Route
          path="/competitions/"
          element={<CompetitionsPage />}
        />
        <Route
          path="*"
          element={
            <PageNotFound textError="Страница не найдена" onBack={handleBack} />
          }
        />
      </Routes>
    </div>
  );
}

export default App;
